import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { 
    CircularProgress, 
    Button, 
    Typography, 
    MenuItem, 
    Link,
    ListItemText,
    ListItemIcon, 
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ReplyIcon from '@mui/icons-material/Reply';

import { TreeItem, SimpleTreeView } from "@mui/x-tree-view";
import { Scrollbars } from "react-custom-scrollbars";
import { useHistory } from "react-router-dom";
import { BreadCrumbs, StyledTreeItem, CircularProgressWithLabel } from "components";
import { EntityProperties } from "panels";
import { getCachedData } from "api/Cache";
import ApiUtils from "api/Utils";
import { FoldersActions } from "actions";
import * as Utils from "services/UtilsService";
import EventsEmitter from "events/EventsEmitter";
import { Public, Folder } from "@mui/icons-material";
import { ReactComponent as ReportIcon } from "assets/images/report.svg";
import commonStyles, {
    defaultBorder,
    folderColor,
    headerHeight,
    viewsHeaderHeight,
    panelBackgroundColor,
} from "styles/common";
import viewsStyles from "styles/views";
import { useDispatch, useSelector } from 'react-redux';
import useSwalWrapper from 'themes/jumbo/@jumbo/vendors/sweetalert2/hooks';
import { getAccessLevel } from "actions/ViewerTools";
import {
    setActiveProjectData,
    resetActiveProjectData
} from "store/auth";
import { StyledMenu } from 'themes/jumbo/app/_components/_core';

const useStyles = makeStyles()((theme: any) => ({
    header: {
        position: "relative",
        flexDirection: "row",
        paddingLeft: 12,
        paddingTop: 12,
        width: "100%",
        alignItems: "center",
        verticalAlign: "top",
    },
    main: {
        width: "100%",
        height: "100%",
        display: "inline-flex",
        borderRight: defaultBorder,
        backgroundColor: panelBackgroundColor,
    },
    firstMain: {
        width: 450,
        height: "100%",
        backgroundColor: "#f7f7f7",
        borderRight: defaultBorder,
    },
    secondMain: {
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
    },
    secondChild: {
        width: "100%",
        height: "100%",
        justifyContent: "center",
    },
    actionButton: {
        marginTop: 3,
        height: 30,
    },
    tree: {
        width: 350,
        height: "100%",
        display: "inline-block",
        paddingTop: theme.spacing(1),
    },
    report: {
        display: "inline-flex",
        verticalAlign: "top",
        justifyContent: "center",
    },
    icon: {
        width: 27,
        height: 27,
        marginRight: theme.spacing(1),
    },
    firstCol: {
        width: "100%",
        maxWidth: "100%",
        textAlign: "left",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        cursor: "pointer",
    },
    secondCol: {
        width: "100%",
        maxWidth: "100%",
        textAlign: "left",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
    },
    result: {
        paddingLeft: 12,
        fontWeight: "bold",
        fontSize: 14,
    },
    polygons: {
        maxWidth: 800,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
    },
    table: {
        padding: 0,
        maxWidth: "100%",
    },
    td: {
        height: 25,
        minWidth: 145,
        maxWidth: 300,
    },
    tdBorder: {
        borderBottom: "solid 1px #d1d1d1",
        padding: 10,
    },
    label: {
        fontWeight: "inherit",
        color: "inherit",
    },
    labelRoot: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0.5, 0),
    },
    labelIcon: {
        marginRight: theme.spacing(1),
    },
    labelText: {
        fontWeight: "inherit",
        flexGrow: 1,
    },
    tabs: {
        width: "100%",
        marginTop: 15,
    },
    tabsContent: {
        padding: 30,
        paddingLeft: 0,
    },
    dataset: {
        paddingLeft: 20,
        paddingTop: 10,
    },
}));

function getIcon(entity: any, classes: any) {
    return <ReportIcon className={classes.icon} />;
}

function renderNodes(list: any, classes: any, common: any, cx: any) {
    if (list.length === 0) {
        return <TreeItem itemId="NoReports" label="No Reports" />;
    }
    if (list[0].type === "PROJECT") {
        list = Utils.sortArrayByProp(list, "name");
    } else {
        //Folders and files inside a project must be rendered by folder first, then files. Those must then be ordered by date DESC (newest first).
        const folderList = list.filter((entity: any) => {
            return entity.type === "FOLDER";
        });
        const fileList = list.filter((entity: any) => {
            return entity.type !== "FOLDER";
        });
        list = Utils.sortArrayByProp(folderList, "date", true, true).concat(
            Utils.sortArrayByProp(fileList, "date", true, true)
        );
    }
    return list.map((entity: any, index: number) => {
        var name = entity.name;
        var type = entity.type;
        if (entity.subFolders) {
            if (entity.type === "PROJECT") {
                entity = entity.subFolders[0];
            }
            return (
                <StyledTreeItem
                    key={"report-folder-" + entity.name + "-" + index}
                    nodeId={"report-folder-" + entity.ID + "-" + index}
                    label={
                        <div className={classes.labelRoot}>
                            {type === "PROJECT" ? (
                                <Public
                                    className={classes.icon}
                                    style={{
                                        color: folderColor,
                                    }}
                                />
                            ) : (
                                <Folder
                                    className={classes.icon}
                                    style={{
                                        color: folderColor,
                                    }}
                                />
                            )}
                            <Typography variant="body2" className={cx(common.mediumLabel, classes.firstCol)}>
                                {name}
                            </Typography>
                        </div>
                    }
                >
                    {renderNodes(Object.values(entity.subFolders), classes, common, cx)}
                </StyledTreeItem>
            );
        } else {
            return (
                <StyledTreeItem
                    key={"report-entity-" + name + "-" + index}
                    nodeId={entity.project + "|" + entity.ID}
                    label={
                        <div className={classes.labelRoot}>
                            {getIcon(entity, common)}
                            <Typography variant="body2" className={cx(common.mediumLabel, classes.firstCol)}>
                                {name}
                            </Typography>
                        </div>
                    }
                />
            );
        }
    });
}

function renderReports(list: any, handleSelect: (evt: any, value: any) => void, classes: any, common: any, cx: any) {
    return (
        <SimpleTreeView className={classes.tree} defaultExpandedItems={["1"]} onItemSelectionToggle={handleSelect}>
            {renderNodes(list, classes, common, cx)}
        </SimpleTreeView>
    );
}

function MainPanel(props: { data: any; selected: any; reload: boolean; setSelected: (selected: any) => void }) {
    const { classes, cx } = useStyles();
    const { classes: common } = commonStyles();
    const { setSelected } = props;
    const [reload, setReload] = useState(false);
    const [filter, setFilter] = useState(Utils.StorageUtils.loadFromSessionStorage("filter") || "all");
    const [reportEntity, setReport] = useState(null);
    const catalog = FoldersActions.getCatalog("0");
    var entities = FoldersActions.getReportHierarchy(catalog);

    useEffect(() => {
        var updateFilter = (filter: string) => {
            setFilter(filter);
            setReload(true);
        };
        EventsEmitter.on("updateFilter", updateFilter);
        return () => {
            EventsEmitter.removeEventListener("updateFilter", setFilter);
        };
    }, [reload]);

    var list = Object.values([...entities]);
    if (filter !== "all") {
        list = list.filter((entity: any) => {
            return entity.ID === filter;
        });
    }
    //console.log("reports.list:", list);
    const handleSelect = (evt: any, value: any) => {
        if (!value.includes("report-folder-")) {
            setSelected(value);
            var reportId = value.split("|")[1];
            var catalog: any = FoldersActions.getCatalog(reportId);
            setReport(catalog);
        }
    };

    return (
        <div className={classes.main}>
            <div className={classes.firstMain}>
                <Scrollbars
                    autoHeight
                    autoHeightMax={"calc(100vh - " + (viewsHeaderHeight + headerHeight) + "px)"}
                    autoHide
                    renderTrackHorizontal={(props: any) => (
                        <div {...props} className="track-horizontal" style={{ display: "none" }} />
                    )}
                    renderThumbHorizontal={(props: any) => (
                        <div {...props} className="thumb-horizontal" style={{ display: "none" }} />
                    )}
                >
                    {renderReports(list, handleSelect, classes, common, cx)}
                </Scrollbars>
            </div>
            <div className={classes.secondMain}>
                <Scrollbars autoHeightMax={"calc(100vh - " + (viewsHeaderHeight + headerHeight) + "px)"} autoHide>
                    <div className={classes.secondChild}>
                        {reportEntity && reportEntity !== null ? (
                            <EntityProperties
                                entity={reportEntity}
                                maxHeight="calc(100vh - 260px)"
                                maxWidth="calc(100% - 10px)"
                            />
                        ) : (
                            ""
                        )}
                    </div>
                </Scrollbars>
            </div>
        </div>
    );
}

const SidePanelMenuItem = ({ icon, label, onClick, disabled = false, id = "" }: { icon?:React.ReactNode; label: string; onClick: (e: any) => void; disabled?: boolean; id?: string}) => {
    return (
      <MenuItem disabled={disabled} disableRipple sx={{ p: 0, mb: 2 }}>
        <Link underline={'none'} onClick={onClick} {...(id ? { id} : {})}>
          {icon && <ListItemIcon sx={{ color: 'inherit' }}>{icon}</ListItemIcon>}
          <ListItemText sx={{textTransform:'uppercase', fontWeight: 600}}>{label}</ListItemText>
        </Link>
      </MenuItem>
    );
};

function SidePanel(props: { data: any; selected: any; setSelected: any }) {
    const { classes } = useStyles();
    const { classes: viewsClasses } = viewsStyles();
    const history = useHistory();
    const { data, selected } = props;
    const projectId = selected.split("|")[0];
    const entityId = selected.split("|")[1];
    const entities = data["entities"];
    const currentProject = entities[projectId];
    const reportEntity = currentProject ? currentProject[entityId] : null;
	const storeAuthData = useSelector((state: any) => state.auth);
    const dispatch = useDispatch();
    const [hasFullAccess, setHasFullAccess] = useState(false);
    const { hasActiveSubscription = false, projectAccessLevel:access_level = "read", isDefaultProject = false } = storeAuthData;

    const Swal = useSwalWrapper();

    useEffect(() => {
        setHasFullAccess(hasActiveSubscription && access_level === 'full')
    }, [access_level, hasActiveSubscription])

    const sweetAlerts = () => {
        Swal.fire({
        title: 'Your account has limited access!',
        text: 'We have limited your access to some features. Please upgrade your current plan to access this feature.',
        imageWidth: 450,
        imageHeight: 200,
        imageAlt: 'Custom image',
        confirmButtonText: "Upgrade my plan",
        showCloseButton: true,
        }).then((result) => {
            result.isConfirmed && history.push("/profile");
        });
    };

    useEffect(() => {
        if(!projectId) {
            dispatch(resetActiveProjectData());
            return;
        }

        dispatch(setActiveProjectData({
            activeProject: projectId,
            projectAccessLevel: getAccessLevel(projectId)
        }));
    }, [projectId])

    const downloadReport = (evt: any) => {
        if (isDefaultProject || !hasFullAccess) {
            !hasFullAccess && sweetAlerts();
            return;
        }
        if (!reportEntity) {
            return;
        }
        var url = ApiUtils.getFileURL(projectId, entityId, "report.csv", "application/csv");
        var link: any = document.createElement("a");
        link.href = url;
        link.download = reportEntity.name + ".csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const goToFolder = (evt: any) => {
        var folder = reportEntity["file_folder"];
        if (folder === "0") {
            folder = projectId;
        }
        EventsEmitter.emit("changeTab", "catalog");
        history.push("/catalog/" + folder);
        history.goForward();
    };
    const deleteSelectedReport = () => {
        if (isDefaultProject || !hasFullAccess) {
            !hasFullAccess && sweetAlerts();
            return;
        }
    };
    return (
        <div className={viewsClasses.side}>
            <div className={classes.header}>
                <StyledMenu sx={{ mb: 2 }}>
                    <SidePanelMenuItem
                        label="Download CSV"
                        icon={<CloudDownloadIcon fontSize={'small'} /> }
                        disabled={selected.length === 0}
                        onClick={downloadReport}
                    />
                    <SidePanelMenuItem
                        label="Go to Folder"
                        icon={<ReplyIcon fontSize={'small'} /> }
                        disabled={selected.length === 0}
                        onClick={goToFolder}
                    />
                    <SidePanelMenuItem
                        label="Delete"
                        icon={<DeleteIcon fontSize={'small'} /> }
                        disabled={selected.length === 0}
                        onClick={deleteSelectedReport}
                    />
                </StyledMenu>
            </div>
        </div>
    );
}

export default function Reports() {
    const { classes: common, cx } = commonStyles();
    const { classes: viewsClasses } = viewsStyles();
    const data: any = getCachedData();
    const [loadData, setLoadData] = useState(false);
    const [selected, setSelected] = useState("");
    const [reload, setReload] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (data.loaded) {
            if (!reload) {
                FoldersActions.buildFolderTree("all", "0", () => {
                    setLoadData(true);
                });
                setReload(true);
            }
        }
    }, [data, selected, reload]);

    useEffect(() => {
        const reload = () => {
            setReload(false);
        };

        const handleProgress = (curProgress: number) => {
            setProgress(curProgress);
        };

        EventsEmitter.on("signalr_update", reload);
        EventsEmitter.on("dataUpdate", reload);
        EventsEmitter.on("projects_progress", handleProgress);
        return () => {
            EventsEmitter.removeEventListener("signalr_update", reload);
            EventsEmitter.removeEventListener("dataUpdate", reload);
            EventsEmitter.removeEventListener("projects_progress", handleProgress);
        };
    }, [reload]);

    if (!data.loaded || !loadData) {
        return progress === 0 ? <CircularProgress /> : <CircularProgressWithLabel value={progress} />;
    }

    return (
        <div className={viewsClasses.main} style={{ width: "100%", height: "100%" }}>
            <div className={cx(common.middle, viewsClasses.topHeader)}>
                <BreadCrumbs label="Reports" className="mainTitle"/>
            </div>
            <div className={viewsClasses.content}>
                <MainPanel data={data} selected={selected} setSelected={setSelected} reload={reload} />
                <SidePanel data={data} selected={selected} setSelected={setSelected} />
            </div>
        </div>
    );
}
